import { Box, Typography } from '@mui/material';
import CustomOverlapImg from 'components/shared/CustomOverlapImg/CustomOverlapImg';
import { useMemo } from 'react';
import zeplinColor from 'theme/Color';
import { Task } from '../../../../../../../model/tasks.model';
import { getInitials } from '../../utils';

type TaskAssigneeProps = {
  task: Task;
};

const normalizeAssignee = (assignee: any) => ({
  Id: assignee.Id,
  Name: assignee.Name,
  Email: assignee?.Email,
  profile_image_url: assignee?.profile_image_url,
  avatar: assignee?.avatar || assignee?.profile_image_url,
});

export const TaskAssignee = ({ task }: TaskAssigneeProps) => {
  const assigneesArray = useMemo(() => {
    let result: Array<{
      Id: string;
      Name: string;
      Email?: string;
      profile_image_url?: string;
      avatar?: string;
    }> | null = null;

    if (task.assignedTo && Array.isArray(task.assignedTo)) {
      const assignedToArray = task.assignedTo as any[];
      result = assignedToArray
        .filter(
          (assignee: any) =>
            assignee && typeof assignee === 'object' && assignee?.Id && assignee?.Name,
        )
        .map(normalizeAssignee);
    }

    if ((!result || result.length === 0) && task.assignedTo) {
      if (Array.isArray(task.assignedTo)) {
        result = task.assignedTo
          .filter((assignee: any) => assignee?.Id && assignee?.Name)
          .map(normalizeAssignee);
      }
    }

    return result;
  }, [task.assignedTo, task.assignedTo]);

  const assigneeInfo = useMemo(() => {
    let assignedToName: string | undefined;
    let avatar: string | undefined;
    if (assigneesArray && assigneesArray.length === 1) {
      const assignee = assigneesArray[0];
      assignedToName = assignee?.Name;
      avatar = assignee?.profile_image_url || assignee?.avatar;
    } else if (task.assignedToName && typeof task.assignedToName === 'string') {
      assignedToName = task.assignedToName;
    } else if (task.assignTo && typeof task.assignTo === 'object' && 'Name' in task.assignTo) {
      assignedToName = (task.assignTo as any).Name;
      avatar = (task.assignTo as any).avatar || (task.assignTo as any).profile_image_url;
    }
    if (!avatar && task.avatar) {
      avatar = task.avatar;
    }

    return {
      name: typeof assignedToName === 'string' ? assignedToName : '',
      avatar,
    };
  }, [assigneesArray, task.assignedToName, task.assignTo, task.avatar]);

  if (assigneesArray && assigneesArray.length > 1) {
    const images = assigneesArray.map(
      (assignee) => assignee?.profile_image_url || assignee?.avatar || '',
    );
    const names = assigneesArray.map((assignee) => assignee?.Name || '');
    return <CustomOverlapImg images={images} name={names} maxVisible={3} />;
  }

  if (!assigneeInfo.name) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: zeplinColor.Neutral5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src='/assets/no-assignee.png' width={24} height={24} alt='No assignee' />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {assigneeInfo.avatar ? (
        <Box
          sx={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            overflow: 'hidden',
            backgroundColor: zeplinColor.Neutral5,
          }}
        >
          <img
            src={assigneeInfo.avatar}
            alt={assigneeInfo.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: zeplinColor.Brand1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant='label2' color={zeplinColor.Neutral1} sx={{ fontSize: '10px' }}>
            {getInitials(assigneeInfo.name)}
          </Typography>
        </Box>
      )}
      <Typography variant='body2'>{assigneeInfo.name}</Typography>
    </Box>
  );
};
